import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, User, MessageSquare, List, Mic, CreditCard, Server, Lock, Share2, Clock, Ban, Users, Trash2, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Policy Section Component
const PolicySection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-[#F3F4F6] flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#0055FF]" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
            {title}
          </h2>
          <div className="text-gray-600 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-sm font-medium"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function YesChefPrivacyPolicy() {
  const lastUpdated = "June 7, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#121212] mb-6" data-testid="privacy-headline">
              Privacy Policy — Yes Chef
            </h1>
            <p className="text-sm text-gray-500">
              Effective Date: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-600 leading-relaxed font-light">
              This Privacy Policy explains how Yes Chef ("we," "us," the "App"), a product of LabrHood Labs, collects, uses, and shares information when you use our mobile application. By using Yes Chef, you agree to the practices described below.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Information We Collect */}
          <AnimatedSection className="mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#121212] mb-8">
              Information We Collect
            </h2>
          </AnimatedSection>

          <PolicySection 
            icon={User} 
            title="Account Information" 
            testId="policy-account"
          >
            <p>
              You can use Yes Chef as an anonymous guest without creating an account. If you choose to sign in, we offer multiple authentication options through Firebase Authentication:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google Sign-In</strong> — provides your name, email address, and profile photo</li>
              <li><strong>Apple Sign-In</strong> — provides a user identifier and, optionally, your name and email</li>
              <li><strong>Email/Password</strong> — you provide an email address and create a password</li>
              <li><strong>Anonymous Guest</strong> — no personal information required; data is stored locally</li>
            </ul>
            <p>
              We maintain an account identifier to save your preferences, lists, and chat history across sessions.
            </p>
          </PolicySection>

          <PolicySection 
            icon={List} 
            title="Shopping Lists and Pantry Data" 
            testId="policy-lists"
          >
            <p>
              When you use Yes Chef's shopping list and pantry management features, we store information about the items you add, including item names, quantities, categories, and dates. This data is used to provide personalized recipe suggestions and help you track what's in your kitchen.
            </p>
          </PolicySection>

          <PolicySection 
            icon={MessageSquare} 
            title="Chat Messages" 
            testId="policy-chat"
          >
            <p>
              Messages you send to the AI chef assistant are transmitted to our AI provider (OpenAI) to generate responses. These messages may include cooking questions, recipe requests, and information about ingredients you have available.
            </p>
            <p>
              Chat history is stored to provide continuity in your conversations and improve your experience. You can delete your chat history at any time through the app settings.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Mic} 
            title="Voice Data (Premium Feature)" 
            testId="policy-voice"
          >
            <p>
              Yes Chef Pro subscribers may access premium voice features powered by ElevenLabs. When you use voice input or listen to voice responses:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Voice input is processed to convert speech to text for the chef assistant</li>
              <li>Text responses may be converted to speech using ElevenLabs' voice synthesis</li>
            </ul>
            <p>
              Voice data is processed in real-time and is not permanently stored by Yes Chef or our voice provider.
            </p>
          </PolicySection>

          <PolicySection 
            icon={CreditCard} 
            title="Subscription and Purchase Information" 
            testId="policy-purchases"
          >
            <p>
              When you subscribe to Yes Chef (Base or Pro tier), your subscription is processed through Apple's App Store and managed by RevenueCat. We receive information about your subscription status (active, expired, tier) but <strong>we do not receive or store your payment card details</strong>.
            </p>
            <p>
              RevenueCat provides us with anonymized subscription analytics and helps manage entitlements across devices.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Server} 
            title="Technical Information" 
            testId="policy-technical"
          >
            <p>
              Like most apps, we and our service providers may collect limited technical information necessary to operate and secure the service, including device type, operating system version, app version, and IP address.
            </p>
          </PolicySection>

          <div className="thin-divider mb-12" />

          {/* How We Use Information */}
          <PolicySection 
            icon={Shield} 
            title="How We Use Information" 
            testId="policy-use"
          >
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide the chef chat assistant and generate AI responses</li>
              <li>Manage your shopping lists and pantry inventory</li>
              <li>Personalize recipe suggestions based on your preferences and available ingredients</li>
              <li>Process and manage your subscription</li>
              <li>Provide customer support</li>
              <li>Improve the app and develop new features</li>
              <li>Protect against fraud and abuse</li>
            </ul>
          </PolicySection>

          {/* How We Share Information */}
          <PolicySection 
            icon={Share2} 
            title="How We Share Information" 
            testId="policy-share"
          >
            <p>
              We share information only as needed to operate the App, with the following service providers:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google/Firebase</strong> — Authentication, database, and analytics</li>
              <li><strong>OpenAI</strong> — AI processing for chef chat responses</li>
              <li><strong>ElevenLabs</strong> — Voice synthesis for premium voice features</li>
              <li><strong>RevenueCat</strong> — Subscription management</li>
              <li><strong>Apple</strong> — App Store and payment processing</li>
            </ul>
            <p className="mt-4">
              <strong>We do not sell your personal information.</strong>
            </p>
          </PolicySection>

          {/* Data Security */}
          <PolicySection 
            icon={Lock} 
            title="Data Security" 
            testId="policy-security"
          >
            <p>
              Data transmitted between the App and our services is encrypted in transit using industry-standard TLS protocols. We use Firebase's security features to protect stored data.
            </p>
          </PolicySection>

          {/* Data Retention */}
          <PolicySection 
            icon={Clock} 
            title="Data Retention" 
            testId="policy-retention"
          >
            <p>
              We retain your data for as long as your account is active or as needed to provide you with our services. You can request deletion of your data at any time (see below).
            </p>
            <p>
              If you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal or legitimate business purposes.
            </p>
          </PolicySection>

          {/* Account Deletion */}
          <PolicySection 
            icon={Trash2} 
            title="Account Deletion" 
            testId="policy-deletion"
          >
            <p>
              You can delete your Yes Chef account directly within the app:
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Open Yes Chef and go to Settings</li>
              <li>Tap "Delete Account"</li>
              <li>Confirm the deletion</li>
            </ol>
            <p>
              This will permanently delete your account, shopping lists, pantry data, and chat history. If you have an active subscription, please cancel it separately through your iPhone Settings to avoid future charges.
            </p>
            <p>
              You may also request account deletion by contacting us at the email below.
            </p>
          </PolicySection>

          {/* Advertising */}
          <PolicySection 
            icon={Ban} 
            title="Advertising" 
            testId="policy-advertising"
          >
            <p>
              Yes Chef does not display third-party advertisements and does not collect or share advertising identifiers.
            </p>
          </PolicySection>

          {/* Children */}
          <PolicySection 
            icon={Users} 
            title="Children's Privacy" 
            testId="policy-children"
          >
            <p>
              Yes Chef is intended for users aged 18 and older. We do not knowingly collect personal information from children under 18. If you believe a child has provided us with personal information, please contact us and we will delete it.
            </p>
          </PolicySection>

          {/* Changes */}
          <AnimatedSection className="mb-12">
            <h3 className="font-heading text-xl font-bold tracking-tight text-[#121212] mb-4">
              Changes to This Policy
            </h3>
            <p className="text-gray-600 leading-relaxed font-light">
              We may update this Privacy Policy from time to time. Material changes will be reflected by updating the effective date above. We encourage you to review this policy periodically.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Contact Section */}
          <AnimatedSection>
            <div className="bg-[#F9FAFB] p-8 sm:p-12">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
                    Contact Us
                  </h2>
                  <p className="text-gray-600 leading-relaxed font-light mb-4">
                    For privacy questions, data requests, or account deletion requests, contact:
                  </p>
                  <a 
                    href="mailto:sravenscroft80@gmail.com" 
                    className="text-[#0055FF] hover:underline font-medium"
                    data-testid="contact-email"
                  >
                    sravenscroft80@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Links */}
          <AnimatedSection className="mt-12">
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="terms-link"
              >
                <a href="/yes-chef/terms-of-use">View Terms of Use</a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="support-link"
              >
                <a href="/yes-chef/support">Support</a>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
